import type { FC } from 'react';
import { Avatar, MatchMedia } from '@george-labs.com/design-system';

import type { Account } from '@shared/products-types';
import { getTitleAlignedWithGeorgeApp } from '@shared/products-general-service';
import { PersonalizationCard as SharedPersonalizationCard } from '@shared/products-components';

import { ProductAvatar } from '../components/ProductAvatar';
import { useProductData } from '../hooks/useProductData';
import { PersonalizationCardCustomizations } from './PersonalizationCardCustomizations';
import { ProductImageUpload } from './ProductImageUpload';

interface Props {
    productId: string;
    subtitle?: string;
    'data-cy'?: string;
}

export const PersonalizationCard: FC<Props> = ({
    subtitle,
    productId,
    'data-cy': dataCy = 'personalisation-card',
}) => {
    const { product: account } = useProductData(productId ?? '');
    const { productI18N, hasImage = false } = account as Account;
    const alias = getTitleAlignedWithGeorgeApp(account) ?? '';

    if (!account) {
        return null;
    }

    const subtitleToDisplay = subtitle || productI18N;

    return (
        <SharedPersonalizationCard
            actions={[
                <PersonalizationCardCustomizations
                    key="customization-actions"
                    productId={productId}
                />,
            ]}
            avatar={
                <MatchMedia max={MatchMedia.TIER.SM}>
                    {({ isMatching }) => {
                        const size = isMatching ? Avatar.SIZE.SIZE_64 : Avatar.SIZE.SIZE_96;

                        return (
                            <ProductImageUpload
                                imageChangeContext="accountDetails"
                                productI18N={productI18N}
                                hasImage={hasImage}
                                productId={productId}
                                productType={account.type}
                                dropdownToggle={
                                    <ProductAvatar product={account} size={size} hasColorPreview />
                                }
                                trigger="productIcon"
                            />
                        );
                    }}
                </MatchMedia>
            }
            subtitle={subtitleToDisplay}
            alias={alias}
            data-cy={dataCy}
        />
    );
};
